"use client";
import { useEffect, useState } from "react";
import Link from "next/link";

type Row = { id: string; name: string; email: string; company: string; total_score: number; created_at: string };

export default function AuditAdmin() {
  const [rows, setRows] = useState<Row[]>([]);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<"total_score"|"created_at">("created_at");
  const [sortDir, setSortDir] = useState<1|-1>(-1);

  useEffect(() => { fetch("/api/audit").then(r => r.json()).then(setRows); }, []);

  const toggleSort = (key: "total_score"|"created_at") => {
    if (sortKey === key) setSortDir(d => (d === 1 ? -1 : 1) as 1|-1);
    else { setSortKey(key); setSortDir(-1); }
  };

  const filtered = rows
    .filter(r => !search || [r.name,r.company,r.email].some(v => v?.toLowerCase().includes(search.toLowerCase())))
    .sort((a,b) => {
      const av = sortKey === "total_score" ? a.total_score : new Date(a.created_at).getTime();
      const bv = sortKey === "total_score" ? b.total_score : new Date(b.created_at).getTime();
      return (av > bv ? 1 : -1) * sortDir;
    });

  const arrow = (k: string) => sortKey === k ? (sortDir === 1 ? " ↑" : " ↓") : "";

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h1 className="text-2xl font-bold">Audit Responses</h1>
        <input
          type="text"
          placeholder="Search name, company, email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="px-3 py-2 rounded-lg bg-[var(--bg)] border border-[var(--border)] text-[var(--text)] outline-none focus:border-[var(--accent)] w-72 text-sm"
        />
      </div>
      {filtered.length === 0 ? (
        <p className="text-[var(--text-muted)]">No responses yet.</p>
      ) : (
        <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] text-[var(--text-muted)] text-left">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Company</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium cursor-pointer select-none" onClick={() => toggleSort("total_score")}>Score{arrow("total_score")}</th>
                <th className="px-4 py-3 font-medium cursor-pointer select-none" onClick={() => toggleSort("created_at")}>Date{arrow("created_at")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const color = r.total_score >= 56 ? "var(--green)" : r.total_score >= 35 ? "var(--yellow)" : "var(--red)";
                return (
                  <tr key={r.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-hover)] transition-colors">
                    <td className="px-4 py-3">
                      <Link href={`/audit/results/${r.id}`} className="text-[var(--accent)] hover:underline no-underline">{r.name}</Link>
                    </td>
                    <td className="px-4 py-3">{r.company || "—"}</td>
                    <td className="px-4 py-3 text-[var(--text-muted)]">{r.email}</td>
                    <td className="px-4 py-3 font-mono font-semibold" style={{ color }}>{r.total_score}/70</td>
                    <td className="px-4 py-3 text-[var(--text-muted)]">{new Date(r.created_at).toLocaleDateString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
